import { Toast } from '@byecode/ui'
import { AVPlayUrlResponse, VariableType } from '@lighthouse/core'
import JSZip from 'jszip'

import type { PreviewFile } from '../../../components/FilePreviewer/types'
import { resolveWechatShareFileName } from '../../../components/FilePreviewer/wechatFileName'
import type { DownloadFileActionConfig } from '../../../types/flow'
import { getUpstreamRealDsId } from '../../flow'
import { getFileNameByUrl, getFileOriginTypeByUrl, getFileTypeByUrl, isTextValue } from '../../helper'
import { getImageFullUrlInApplication } from '../../image'
import type { ExtraParams } from './common'
import { getArgsResult, getRecordResult } from './common'

type DownloadFileInfo = {
    fieldName: string
    files: PreviewFile[]
    fetchAVPlayUrl?: (originalUrl: string, isOrigin?: boolean) => Promise<AVPlayUrlResponse>
}

/**
 * 下载动作与动作流解析附件变量
 * @param config 下载动作配置
 * @param extraParams
 * @returns
 */
export const resolveDownloadFileUrls = (config: DownloadFileActionConfig, extraParams?: ExtraParams): DownloadFileInfo | undefined => {
    const { fileUrl } = config

    if (!fileUrl) {
        return
    }
    const appId = extraParams?.appId
    const dataSourceList = extraParams?.dataSourceList || []
    const fetchAVPlayUrl = extraParams?.fetchAVPlayUrl
    switch (fileUrl.type) {
        case VariableType.UPSTREAM: {
            const { upstreamVariable } = fileUrl

            if (!upstreamVariable || !upstreamVariable.nodeId || !upstreamVariable.fieldId) {
                return
            }

            const { nodeId, fieldId } = upstreamVariable

            const record = getRecordResult(extraParams?.upstreamNodesResult?.[nodeId])

            const dsId = getUpstreamRealDsId(nodeId, extraParams?.workflow?.content?.nodes || [])

            const dataSource = dataSourceList?.find(({ id }) => id === dsId)
            const fieldName = dataSource?.schema[fieldId]?.name ?? ''
            const fieldValue = record?.content?.[fieldId]?.value
            if (!fieldValue) {
                return
            }
            if (Array.isArray(fieldValue)) {
                const files = fieldValue.map(url => {
                    const fileName = getFileNameByUrl(url.toString()) || ''
                    const fullUrl = getImageFullUrlInApplication(appId, url.toString())
                    return {
                        name: fileName,
                        type: getFileTypeByUrl(fileName),
                        url: fullUrl
                    }
                })
                return { fieldName, files, fetchAVPlayUrl }
            }
            if (isTextValue(fieldValue)) {
                const name = getFileNameByUrl(fieldValue.toString()) || ''
                const fullUrl = getImageFullUrlInApplication(appId, fieldValue.toString())
                const files = [
                    {
                        name,
                        type: getFileTypeByUrl(name),
                        url: fullUrl
                    }
                ]
                return { fieldName, files, fetchAVPlayUrl }
            }
            return
        }
        case VariableType.ARG_VARIABLE: {
            const { argVariable } = fileUrl

            if (!argVariable || !argVariable.nodeId || !argVariable.argId) {
                return
            }

            const { nodeId, argId } = argVariable

            const args = getArgsResult(extraParams?.upstreamNodesResult?.[nodeId])
            const arg = args?.find(item => item.id === argId)
            const argValue = arg?.value
            if (!arg) {
                return
            }
            if (Array.isArray(argValue)) {
                const files = argValue.map(url => {
                    const fileName = getFileNameByUrl(url.toString()) || ''
                    const fullUrl = getImageFullUrlInApplication(appId, url.toString())
                    return {
                        name: fileName,
                        type: getFileTypeByUrl(fileName),
                        url: fullUrl
                    }
                })
                return { fieldName: arg.name, files, fetchAVPlayUrl }
            }
            if (isTextValue(argValue)) {
                const name = getFileNameByUrl(argValue.toString()) || ''
                const fullUrl = getImageFullUrlInApplication(appId, argValue.toString())
                const files = [
                    {
                        name,
                        type: getFileTypeByUrl(name),
                        url: fullUrl
                    }
                ]
                return { fieldName: arg.name, files, fetchAVPlayUrl }
            }
            return
        }
        case VariableType.VALUE: {
            const { valueVariable } = fileUrl

            if (!valueVariable) {
                return
            }

            const { type, value } = valueVariable

            if (Array.isArray(value)) {
                const files = value.map(url => {
                    const fileName = getFileNameByUrl(url.toString()) || ''
                    const fullUrl = getImageFullUrlInApplication(appId, url.toString())
                    return {
                        name: fileName,
                        type: getFileTypeByUrl(fileName),
                        url: fullUrl
                    }
                })
                return { fieldName: '', files, fetchAVPlayUrl }
            }
            if (isTextValue(value)) {
                const name = getFileNameByUrl(value.toString()) || ''
                const fullUrl = getImageFullUrlInApplication(appId, value.toString())
                const files = [
                    {
                        name,
                        type: getFileTypeByUrl(name),
                        url: fullUrl
                    }
                ]
                return { fieldName: '', files, fetchAVPlayUrl }
            }
            return
        }

        default: {
            const viewRecord = extraParams?.viewRecord
            const formRecord = extraParams?.viewRecord
            const imageInfo = extraParams?.imageInfo
            const scope = extraParams?.scope
            const fieldValue = extraParams?.parseVariable2Field(fileUrl, { viewRecord, formRecord, imageInfo, scope })

            if (!fieldValue) {
                return
            }

            const { value: fieldCellValue, name: fieldName } = fieldValue

            if (Array.isArray(fieldCellValue)) {
                const files = fieldCellValue.map(url => {
                    const fileName = getFileNameByUrl(url.toString()) || ''
                    const fullUrl = getImageFullUrlInApplication(appId, url.toString())
                    return {
                        name: fileName,
                        type: getFileTypeByUrl(fileName),
                        url: fullUrl
                    }
                })
                return { fieldName, files, fetchAVPlayUrl }
            }
            if (isTextValue(fieldCellValue)) {
                const name = getFileNameByUrl(fieldCellValue.toString()) || ''
                const fullUrl = getImageFullUrlInApplication(appId, fieldCellValue.toString())
                const files = [
                    {
                        name,
                        type: getFileTypeByUrl(name),
                        url: fullUrl
                    }
                ]
                return { fieldName, files, fetchAVPlayUrl }
            }
            return
        }
    }
}

const triggerBrowserDownload = (href: string, fileName: string) => {
    const downloadLink = document.createElement('a')
    downloadLink.href = href
    downloadLink.download = fileName
    downloadLink.rel = 'noopener'
    downloadLink.click()
    downloadLink.remove()
}

export const downloadSingleFileWithUrl = async (params: { fileName?: string; url: string }) => {
    const { fileName = '', url } = params
    const namedFile = resolveWechatShareFileName({ name: fileName || '文件', url })
    triggerBrowserDownload(url, namedFile)
}

const downloadFileWithUrl = async (params: { fileName?: string; url: string }) => {
    const { fileName = '', url } = params

    try {
        const response = await fetch(url, { cache: 'no-cache' })
        const blob = await response.blob()

        // 创建一个隐藏的<a>元素，并设置其属性
        const downloadLink = document.createElement('a')
        downloadLink.href = window.URL.createObjectURL(blob)
        downloadLink.download = fileName

        // 触发点击事件以下载文件
        downloadLink.click()
        downloadLink.remove()

        // 释放URL对象
        window.URL.revokeObjectURL(downloadLink.href)
    } catch (error) {
        console.error('Error downloading file:', error)
    }
}

/**
 * 获取带播放凭证的文件
 * @param files 文件列表
 * @param fetchAVPlayUrl 获取播放凭证的函数
 * @returns 带播放凭证的文件列表
 */
export const getAuthFile = async (
    file: PreviewFile,
    fetchAVPlayUrl?: (originalUrl: string, isOrigin?: boolean) => Promise<AVPlayUrlResponse>
) => {
    if (!fetchAVPlayUrl) {
        return file
    }
    if (
        getFileOriginTypeByUrl(file.type) === 'video' ||
        getFileOriginTypeByUrl(file.type) === 'audio' ||
        file.type === 'video' ||
        file.type === 'audio'
    ) {
        const auth = await fetchAVPlayUrl(file.url, true)
        return {
            ...file,
            url: auth.playUrl
        }
    }
    return file
}

/**
 * 获取带播放凭证的文件
 * @param files 文件列表
 * @param fetchAVPlayUrl 获取播放凭证的函数
 * @returns 带播放凭证的文件列表
 */
export const getAuthFiles = async (
    files: PreviewFile[],
    fetchAVPlayUrl?: (originalUrl: string, isOrigin?: boolean) => Promise<AVPlayUrlResponse>
) => {
    if (!fetchAVPlayUrl) {
        return files
    }
    return Promise.all(files.map(async file => await getAuthFile(file, fetchAVPlayUrl)))
}
/**
 * 专用于下载动作、动作流中附件内容
 * @param downloadFileUrl
 */
export const downloadFile = async (downloadFileParams: DownloadFileInfo) => {
    const { fieldName, files, fetchAVPlayUrl } = downloadFileParams
    let authFiles: PreviewFile[] = files
    if (fetchAVPlayUrl) {
        authFiles = await getAuthFiles(files, fetchAVPlayUrl)
    }
    Toast.info('开始下载文件...')
    // 单个文件直接下载
    if (authFiles.length === 1) {
        // downloadSingleFileWithUrl({ url: files[0].url, fileName: files[0].name })
        await downloadSingleFileWithUrl({ url: authFiles[0].url, fileName: resolveWechatShareFileName(authFiles[0]) })
    }

    if (authFiles.length > 1) {
        const zip = new JSZip()
        const folder = zip.folder(fieldName)
        const withBlobFiles = await Promise.all(
            authFiles.map(async file => ({
                name: file.name,
                blob: await fetch(file.url, { cache: 'no-cache' }).then(response => response.blob())
            }))
        )

        for (const withBlobFile of withBlobFiles) {
            folder?.file(withBlobFile.name, withBlobFile.blob)
        }

        const content = await zip.generateAsync({ type: 'blob' })

        const url = URL.createObjectURL(content)
        const fileName = `${fieldName}.zip`

        await downloadFileWithUrl({ fileName, url })
    }
}
